import React from 'react';
import {
  Grid,
  Typography,
  Box,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Chip,
  Paper,
  FormHelperText,
} from '@mui/material';
import {
  Wifi as WifiIcon,
  Kitchen as KitchenIcon,
  LocalParking as ParkingIcon,
  AcUnit as AcIcon,
  Whatshot as HeatingIcon,
  Tv as TvIcon,
  LocalLaundryService as LaundryIcon,
  Pool as PoolIcon,
  FitnessCenter as GymIcon,
  Pets as PetsIcon,
  SmokingRooms as SmokingIcon,
  Accessible as AccessibleIcon,
  FreeBreakfast as BreakfastIcon,
  Security as SecurityIcon,
  LocalFireDepartment as FireIcon,
  MedicalServices as FirstAidIcon,
  CameraOutdoor as CameraIcon,
} from '@mui/icons-material';
import { ListingForm as ListingFormType } from '@/types';

interface AmenitiesTabProps {
  formData: ListingFormType;
  errors: Record<string, string>;
  onFormDataChange: (updates: Partial<ListingFormType>) => void;
  onErrorsChange: (errors: Record<string, string>) => void;
  clearErrors: (fields: string[]) => void;
}

const AMENITY_CATEGORIES = {
  essentials: {
    title: 'Essentials',
    icon: WifiIcon,
    items: [
      { value: 'WiFi', label: 'WiFi', icon: WifiIcon },
      { value: 'Kitchen', label: 'Kitchen', icon: KitchenIcon },
      { value: 'Parking', label: 'Free parking', icon: ParkingIcon },
      { value: 'Air Conditioning', label: 'Air conditioning', icon: AcIcon },
      { value: 'Heating', label: 'Heating', icon: HeatingIcon },
      { value: 'TV', label: 'TV', icon: TvIcon },
    ],
  },
  features: {
    title: 'Features',
    items: [
      { value: 'Washer', label: 'Washer', icon: LaundryIcon },
      { value: 'Dryer', label: 'Dryer', icon: LaundryIcon },
      { value: 'Pool', label: 'Pool', icon: PoolIcon },
      { value: 'Hot Tub', label: 'Hot tub', icon: PoolIcon },
      { value: 'Gym', label: 'Gym', icon: GymIcon },
      { value: 'Breakfast Included', label: 'Breakfast included', icon: BreakfastIcon },
    ],
  },
  policies: {
    title: 'Guest Policies',
    items: [
      { value: 'Pet Friendly', label: 'Pet friendly', icon: PetsIcon },
      { value: 'Smoking Allowed', label: 'Smoking allowed', icon: SmokingIcon },
    ],
  },
};

const SAFETY_FEATURES = [
  { value: 'smoke_detector', label: 'Smoke detector' },
  { value: 'carbon_monoxide_detector', label: 'Carbon monoxide detector' },
  { value: 'fire_extinguisher', label: 'Fire extinguisher' },
  { value: 'first_aid_kit', label: 'First aid kit' },
  { value: 'security_cameras', label: 'Security cameras (exterior)' },
  { value: 'lockbox', label: 'Lockbox' },
  { value: 'security_system', label: 'Security system' },
];

const ACCESSIBILITY_OPTIONS = [
  {
    key: 'wheelchairAccessible',
    label: 'Wheelchair accessible',
    description: 'Wide doorways and accessible bathroom',
  },
  {
    key: 'stepFreeAccess',
    label: 'Step-free guest entrance',
    description: 'No steps or stairs to enter',
  },
  {
    key: 'accessibleParking',
    label: 'Accessible parking spot',
    description: 'Designated accessible parking',
  },
  {
    key: 'accessibleBathroom',
    label: 'Accessible bathroom',
    description: 'Roll-in shower or accessible features',
  },
];

export const AmenitiesTab: React.FC<AmenitiesTabProps> = ({
  formData,
  errors,
  onFormDataChange,
  clearErrors,
}) => {
  const handleAmenityChange = (amenity: string, checked: boolean) => {
    const currentAmenities = formData.amenities || [];
    const updatedAmenities = checked
      ? [...currentAmenities, amenity]
      : currentAmenities.filter(a => a !== amenity);
    
    onFormDataChange({ amenities: updatedAmenities });
    clearErrors(['amenities']);
  };

  const handleSafetyFeatureChange = (feature: string, checked: boolean) => {
    const currentFeatures = formData.safetyFeatures || [];
    const updatedFeatures = checked
      ? [...currentFeatures, feature]
      : currentFeatures.filter(f => f !== feature);
    
    onFormDataChange({ safetyFeatures: updatedFeatures });
    clearErrors(['safetyFeatures']);
  };

  const handleAccessibilityChange = (key: string, checked: boolean) => {
    const currentAccessibility = formData.accessibility || {};
    const updatedAccessibility = {
      ...currentAccessibility,
      [key]: checked,
    };
    
    onFormDataChange({ accessibility: updatedAccessibility });
    clearErrors(['accessibility']);
  };

  const isAmenitySelected = (amenity: string) => {
    return formData.amenities?.includes(amenity) || false;
  };

  const isSafetyFeatureSelected = (feature: string) => {
    return formData.safetyFeatures?.includes(feature) || false;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Amenities & Features
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select the amenities and features available at your property to help guests understand what's included.
      </Typography>

      {/* Amenities by Category */}
      {Object.entries(AMENITY_CATEGORIES).map(([categoryKey, category]) => (
        <Box key={categoryKey} sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom>
            {category.title}
          </Typography>
          
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {category.items.map((item) => {
                const IconComponent = item.icon;
                return (
                  <Grid item xs={12} sm={6} md={4} key={item.value}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isAmenitySelected(item.value)}
                          onChange={(e) => handleAmenityChange(item.value, e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconComponent fontSize="small" color="action" />
                          {item.label}
                        </Box>
                      }
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Box>
      ))}

      <Divider sx={{ my: 4 }} />

      {/* Safety Features */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Safety Features
        </Typography>
        <FormHelperText sx={{ mb: 2 }}>
          Select the safety equipment and features available at your property.
        </FormHelperText>
        
        <Paper variant="outlined" sx={{ p: 2 }}>
          <FormControl component="fieldset" fullWidth>
            <FormGroup>
              <Grid container spacing={1}>
                {SAFETY_FEATURES.map((feature) => (
                  <Grid item xs={12} sm={6} key={feature.value}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isSafetyFeatureSelected(feature.value)}
                          onChange={(e) => handleSafetyFeatureChange(feature.value, e.target.checked)}
                        />
                      }
                      label={feature.label}
                    />
                  </Grid>
                ))}
              </Grid>
            </FormGroup>
          </FormControl>
        </Paper>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Accessibility */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom>
          Accessibility Features
        </Typography>
        <FormHelperText sx={{ mb: 2 }}>
          Select accessibility features that make your property more inclusive for guests with disabilities.
        </FormHelperText>
        
        <Paper variant="outlined" sx={{ p: 2 }}>
          <FormControl component="fieldset" fullWidth>
            <FormGroup>
              {ACCESSIBILITY_OPTIONS.map((option) => (
                <Box key={option.key} sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.accessibility?.[option.key as keyof typeof formData.accessibility] || false}
                        onChange={(e) => handleAccessibilityChange(option.key, e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {option.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              ))}
            </FormGroup>
          </FormControl>
        </Paper>
      </Box>

      {/* Selected Amenities Summary */}
      {formData.amenities && formData.amenities.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Selected Amenities ({formData.amenities.length})
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {formData.amenities.map((amenity) => (
              <Chip
                key={amenity}
                label={amenity}
                size="small"
                onDelete={() => handleAmenityChange(amenity, false)}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AmenitiesTab;